import { ReactNode, useCallback, useEffect, useState, type WheelEvent } from "react";
import { styled } from "styled-components";
import { FormInstance } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import toast from "react-hot-toast";
import {
  MdCategory,
  MdEdit,
  MdEmojiEvents,
  MdImage,
  MdInfoOutline,
  MdMovieFilter,
  MdNotes,
  MdPersonOutline,
  MdShield,
  MdWarningAmber,
} from "react-icons/md";
import { colors } from "../../../../theme/colors";
import {
  Award as AwardModel,
  FilmProfile,
} from "../../../../models/film-profile.model";
import { valueToLabel } from "../../../../utils/functions/misc/valueToLabel";
import { useAtomValue } from "jotai";
import { masterAtom } from "../../../../atoms/masters.atom";
import { countries } from "../../../../data/countries";
import { Poster } from "../../../../types/Poster";
import { SetState } from "../../../../types/SetState";
import { wait } from "../../../../utils/functions/misc/wait";
import { Section, card } from "../../components/section-card.component";
import { config } from "../../../../config/config";

const requiredFields: (keyof FilmProfile)[] = [
  "title",
  "custom_name",
  "category",
  "genre",
  "language",
  "tagline",
  "synopsis",
  "duration",
  "date_of_completion",
  "shooting_format",
  "aspect_ratio",
  "language_rating",
  "harmful_rating",
  "hateful_rating",
  "adult_content_rating",
  "violence_rating",
  "drug_rating",
];

type Person = {
  name: string;
  user?: string;
  id?: string;
  type?: "professional" | "business";
  logo?: string;
};

export const ReviewStep = ({
  form,
  directors,
  producers,
  cast,
  crew,
  awards,
  verticalPoster,
  horizontalPoster,
  productionStills,
  completedSteps,
  setCurrentStep,
  synopsis,
  visible = true,
  setPosterErrors,
  keywords,
}: {
  loaded: boolean;
  synopsis: string;
  visible?: boolean;
  awards: AwardModel[];
  form: FormInstance<FilmProfile>;
  completedSteps: (boolean | null)[];
  directors: (Person & { biography: string })[];
  producers: (Person & { filmography: string })[];
  cast: (Person & { character: string })[];
  crew: (Person & { role: string })[];
  verticalPoster: Poster[];
  horizontalPoster: Poster[];
  productionStills: Poster[];
  setCurrentStep: SetState<number>;
  setPosterErrors: SetState<[boolean, boolean, boolean]>;
  keywords: string[];
}) => {
  const master = useAtomValue(masterAtom);

  const [film, setFilm] = useState<Partial<FilmProfile>>({});
  const [errorFields, setErrorFields] = useState<
    Record<keyof FilmProfile, boolean | null>
  >({} as Record<keyof FilmProfile, boolean | null>);

  useEffect(() => {
    const filmDetails = { ...form.getFieldsValue(true) };
    if (Object.keys(filmDetails).length > 0) setFilm(filmDetails);
  }, [form]);

  useEffect(() => {
    const errors = {} as Record<keyof FilmProfile, boolean | null>;
    if (film) {
      for (const field of requiredFields) {
        errors[field] = field === "synopsis" ? !synopsis : !film[field];
      }
      setErrorFields(errors);
    }
  }, [film, synopsis]);

  const onGoToStep = useCallback(
    async (step: number) => {
      setCurrentStep(step);
      await wait(500);
      form.setFields(
        Object.entries(errorFields).map(([field, error]) => ({
          name: field,
          errors: error ? ["This field is required!"] : [],
        }))
      );
    },
    [setCurrentStep, errorFields, form]
  );

  const onReviewWheel = useCallback((e: WheelEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey) return;
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    const root = e.currentTarget;
    let el = e.target as HTMLElement | null;
    for (; el && el !== root; el = el.parentElement) {
      if (!(el instanceof HTMLElement)) break;
      const cs = getComputedStyle(el);
      const yScrollable =
        (cs.overflowY === "auto" ||
          cs.overflowY === "scroll" ||
          cs.overflowY === "overlay") &&
        el.scrollHeight > el.clientHeight + 1;
      const xScrollable =
        (cs.overflowX === "auto" ||
          cs.overflowX === "scroll" ||
          cs.overflowX === "overlay") &&
        el.scrollWidth > el.clientWidth + 1;
      if (yScrollable) {
        const top = el.scrollTop;
        const max = el.scrollHeight - el.clientHeight;
        if ((e.deltaY < 0 && top > 0) || (e.deltaY > 0 && top < max - 0.5)) {
          return;
        }
      }
      if (xScrollable && Math.abs(e.deltaX) >= Math.abs(e.deltaY)) {
        const left = el.scrollLeft;
        const maxL = el.scrollWidth - el.clientWidth;
        if (
          (e.deltaX < 0 && left > 0) ||
          (e.deltaX > 0 && left < maxL - 0.5)
        ) {
          return;
        }
      }
    }
    const scroller = document.scrollingElement ?? document.documentElement;
    scroller.scrollTop += e.deltaY;
    e.preventDefault();
  }, []);

  const duration = (() => {
    if (!film.duration) return "";
    if (typeof film.duration === "string") return film.duration;
    if (film.duration.mm && film.duration.ss) {
      return `${film.duration.mm.toString().padStart(2, "0")}:${film.duration.ss
        .toString()
        .padStart(2, "0")}`;
    }
    return "";
  })();

  const heroPoster =
    verticalPoster.find((p) => p.type === 0 && p.url)?.url ??
    verticalPoster.find((p) => p.url)?.url ??
    horizontalPoster.find((p) => p.url)?.url ??
    null;

  return (
    <Wrapper
      style={{ display: visible ? "flex" : "none" }}
      onWheel={onReviewWheel}
    >
      <Hero>
        {heroPoster ? <HeroPoster src={heroPoster} /> : <HeroPosterPlaceholder>No poster</HeroPosterPlaceholder>}
        <HeroBody>
          <HeroEyebrow>Final review</HeroEyebrow>
          <HeroTitle>{film.title || "Untitled film"}</HeroTitle>
          {film.tagline ? <HeroTagline>{film.tagline}</HeroTagline> : null}
          <HeroMeta>
            {film.custom_name ? (
              <HeroMetaItem>
                {config.public_site_host}/{film.custom_name}
              </HeroMetaItem>
            ) : null}
            {film.category ? (
              <HeroChip>{valueToLabel("film_category", film.category, master)}</HeroChip>
            ) : null}
            {duration ? <HeroChip>{duration} min</HeroChip> : null}
            {film.language ? (
              <HeroChip>{_.capitalize(film.language)}</HeroChip>
            ) : null}
          </HeroMeta>
        </HeroBody>
      </Hero>

      <TwoColRow>
        <Section
          icon={<MdInfoOutline />}
          title="Basic Details"
          subtitle="Title, language, category, genre and synopsis."
          fillHeight
          action={
            <EditButton
              $error={completedSteps[0] === false}
              onClick={() => onGoToStep(0)}
            >
              <MdEdit /> Edit
            </EditButton>
          }
        >
          <KvGrid>
            <Kv label="Original Title" value={film.title} required />
            <Kv label="English Title" value={film.english_title} />
            <Kv label="Custom URL" value={film.custom_name} required />
            <Kv
              label="Category"
              value={valueToLabel("film_category", film.category, master)}
              required
            />
            <Kv
              label="Genre"
              value={film.genre?.map(_.capitalize)?.join(", ")}
              required
            />
            <Kv
              label="Original Language"
              value={_.capitalize(film.language ?? "")}
              required
            />
            <Kv label="Tagline" value={film.tagline} required fullWidth />
            <Kv
              label="Synopsis"
              value={synopsis}
              required
              fullWidth
              multiline
            />
          </KvGrid>
        </Section>

        <Section
          icon={<MdMovieFilter />}
          title="Technical Details"
          subtitle="Format, duration, country of production."
          fillHeight
          action={
            <EditButton
              $error={completedSteps[1] === false}
              onClick={() => {
                onGoToStep(1);
                if (!duration) {
                  toast.error("Please enter a film duration!");
                  form.setFields([
                    { name: ["duration", "mm"], errors: [""] },
                    { name: ["duration", "ss"], errors: [""] },
                  ]);
                }
              }}
            >
              <MdEdit /> Edit
            </EditButton>
          }
        >
          <KvGrid>
            <Kv label="Duration (mm:ss)" value={duration} required />
            <Kv
              label="Date of Completion"
              value={
                film.date_of_completion
                  ? dayjs(film.date_of_completion).format("DD-MM-YYYY")
                  : ""
              }
              required
            />
            <Kv
              label="Shooting Format"
              value={valueToLabel(
                "shooting_format",
                film.shooting_format,
                master
              )}
              required
            />
            <Kv
              label="Original Aspect Ratio"
              value={valueToLabel("aspect_ratio", film.aspect_ratio, master)}
              required
            />
            <Kv
              label="Country"
              value={(film.country?.split("-") ?? [""])[0]}
              required
            />
            <Kv label="Keywords" value={keywords.join(", ")} />
          </KvGrid>
        </Section>
      </TwoColRow>

      <Section
        icon={<MdShield />}
        title="Censor Ratings"
        subtitle="Content advisories across six categories."
        action={
          <EditButton
            $error={completedSteps[2] === false}
            onClick={() => onGoToStep(2)}
          >
            <MdEdit /> Edit
          </EditButton>
        }
      >
        <RatingGrid>
          <RatingPill
            label="Language"
            value={valueToLabel(
              "language_ratings",
              film.language_rating,
              master
            )}
          />
          <RatingPill
            label="Violence"
            value={valueToLabel(
              "violence_ratings",
              film.violence_rating,
              master
            )}
          />
          <RatingPill
            label="Hateful & Derogatory"
            value={valueToLabel(
              "hateful_ratings",
              film.hateful_rating,
              master
            )}
          />
          <RatingPill
            label="Adult Content"
            value={valueToLabel(
              "adult_content_ratings",
              film.adult_content_rating,
              master
            )}
          />
          <RatingPill
            label="Drugs"
            value={valueToLabel("drugs_ratings", film.drug_rating, master)}
          />
          <RatingPill
            label="Harmful / Dangerous Acts"
            value={valueToLabel(
              "harmful_ratings",
              film.harmful_rating,
              master
            )}
          />
        </RatingGrid>
      </Section>

      <Section
        icon={<MdCategory />}
        title="Cast & Crew"
        subtitle="Everyone involved in making this film."
        action={
          <EditButton
            $error={completedSteps[3] === false}
            onClick={() => {
              onGoToStep(3);
              if (!directors.filter((d) => !!d.name).length)
                toast.error("Please add at least one director!");
              if (!producers.filter((p) => !!p.name).length)
                toast.error("Please add at least one producer!");
              const invalidCast = cast.filter((c) => !!c.name && !c.character);
              for (const m of invalidCast) {
                form.setFields([
                  {
                    name: `character.${m.id}`,
                    errors: ["Please enter a character name!"],
                  },
                ]);
              }
              const invalidCrew = crew
                .map((c, i) => ({ c, i }))
                .filter(({ c }) => !!c.name && !c.role);
              for (const { i } of invalidCrew) {
                form.setFields([
                  { name: `role.${i}`, errors: ["Please select a role!"] },
                ]);
              }
            }}
          >
            <MdEdit /> Edit
          </EditButton>
        }
      >
        <CrewGrid>
          <PeopleGroup
            title="Directors"
            empty="No directors added"
            error={!directors.filter((d) => !!d.name).length}
            people={directors
              .filter((d) => !!d.name)
              .map((d) => ({
                key: d.id ?? d.name,
                name: d.name,
                logo: d.logo,
                meta: "",
              }))}
          />
          <PeopleGroup
            title="Producers"
            empty="No producers added"
            error={!producers.filter((p) => !!p.name).length}
            people={producers
              .filter((p) => !!p.name)
              .map((p) => ({
                key: p.id ?? p.name,
                name: p.name,
                logo: p.logo,
                meta: "",
              }))}
          />
          <PeopleGroup
            title="Cast"
            empty="No cast added"
            people={cast.map((c) => ({
              key: c.id ?? c.name,
              name: c.name,
              logo: c.logo,
              meta: c.character,
              error: !!c.name && !c.character,
            }))}
          />
          <PeopleGroup
            title="Crew"
            empty="No crew added"
            people={crew.map((c) => ({
              key: c.id ?? c.name,
              name: c.name,
              logo: c.logo,
              meta: String(
                valueToLabel("crew_role", c.role, master) ?? c.role ?? ""
              ),
              error: !!c.name && !c.role,
            }))}
          />
        </CrewGrid>
      </Section>

      <Section
        icon={<MdEmojiEvents />}
        title="Awards & Festivals"
        subtitle="Recognition received by the film."
        action={
          <EditButton
            $error={completedSteps[4] === false}
            onClick={() => onGoToStep(4)}
          >
            <MdEdit /> Edit
          </EditButton>
        }
      >
        {awards.length === 0 ? (
          <EmptyText>No awards added.</EmptyText>
        ) : (
          <AwardList>
            {awards.map((award, i) => (
              <AwardRow key={award.id ?? `award-${i}`}>
                <AwardYear>
                  {typeof award.award_year === "number"
                    ? award.award_year
                    : award.award_year.format("YYYY")}
                </AwardYear>
                <AwardBody>
                  <AwardName>{award.name_of_festival}</AwardName>
                  <AwardMeta>
                    {countries.find(
                      ({ value }) =>
                        value.split("-")[1].toLowerCase() ===
                        award.country?.toLowerCase()
                    )?.label ?? award.country ?? ""}
                    {" · "}
                    {valueToLabel("award_category", award.award_category, master)}
                  </AwardMeta>
                  {award.award_winner_name ? (
                    <AwardWinner>
                      Winner: <strong>{award.award_winner_name}</strong>
                    </AwardWinner>
                  ) : null}
                </AwardBody>
                <StatusPill>
                  {valueToLabel("festival_status", award.award_status, master)}
                </StatusPill>
              </AwardRow>
            ))}
          </AwardList>
        )}
      </Section>

      <Section
        icon={<MdImage />}
        title="Posters & Stills"
        subtitle="Artwork and behind-the-scenes images."
        action={
          <EditButton
            $error={completedSteps[5] === false}
            onClick={() => {
              onGoToStep(5);
              const errs: [boolean, boolean, boolean] = [false, false, false];
              if (!verticalPoster.filter((p) => p.type === 0).length) {
                errs[0] = true;
                toast.error("Please upload at least one vertical poster!");
              }
              if (!horizontalPoster.filter((p) => p.type === 0).length) {
                errs[1] = true;
                toast.error("Please upload at least one horizontal poster!");
              }
              setPosterErrors(errs);
            }}
          >
            <MdEdit /> Edit
          </EditButton>
        }
      >
        <PosterGroup
          label="Vertical posters"
          posters={verticalPoster}
          error={!verticalPoster.filter((p) => p.type === 0).length}
          emptyMessage="No vertical posters uploaded"
        />
        <PosterGroup
          label="Horizontal posters"
          posters={horizontalPoster}
          error={!horizontalPoster.filter((p) => p.type === 0).length}
          emptyMessage="No horizontal posters uploaded"
        />
        <PosterGroup
          label="Production stills"
          posters={productionStills}
          emptyMessage="No production stills uploaded"
        />
      </Section>

      <Section
        icon={<MdNotes />}
        title="Statements & Notes"
        subtitle="Context shared with distributors."
      >
        <KvGrid>
          <Kv
            label="Has been screened?"
            value={film.is_screened ? "Yes" : "No"}
          />
          <Kv
            label="Director's Statement"
            value={film.director_statement}
            fullWidth
            multiline
          />
          <Kv
            label="Producer's Statement"
            value={film.producer_statement}
            fullWidth
            multiline
          />
          <Kv
            label="Other Information"
            value={film.other_information}
            fullWidth
            multiline
          />
        </KvGrid>
      </Section>
    </Wrapper>
  );
};

const Kv = ({
  label,
  value,
  required,
  fullWidth,
  multiline,
}: {
  label: string;
  value?: ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  multiline?: boolean;
}) => {
  const missing = required && !value;
  return (
    <KvShell $fullWidth={fullWidth}>
      <KvLabel $error={!!missing}>
        {label}
        {required ? <RequiredMark>*</RequiredMark> : null}
      </KvLabel>
      <KvValue $missing={!value} $multiline={multiline}>
        {value || (missing ? "Required" : "—")}
      </KvValue>
    </KvShell>
  );
};

const RatingPill = ({
  label,
  value,
}: {
  label: string;
  value?: ReactNode;
}) => (
  <RatingShell $missing={!value}>
    <RatingLabel>{label}</RatingLabel>
    <RatingValue $missing={!value}>{value || "Required"}</RatingValue>
  </RatingShell>
);

const PeopleGroup = ({
  title,
  people,
  empty,
  error,
}: {
  title: string;
  people: { key: string; name: string; logo?: string; meta?: string; error?: boolean }[];
  empty: string;
  error?: boolean;
}) => (
  <PeopleShell>
    <PeopleHead $error={!!error}>
      <span>{title}</span>
      <PeopleCount>{people.length}</PeopleCount>
    </PeopleHead>
    {people.length === 0 ? (
      <PeopleEmpty $error={!!error}>
        {error ? <MdWarningAmber /> : <MdPersonOutline />}
        <span>{empty}</span>
      </PeopleEmpty>
    ) : (
      <PeopleList>
        {people.map((p) => (
          <PeopleItem key={p.key}>
            <PeopleAvatar src={p.logo || "/images/ui/avatar.png"} />
            <PeopleText>
              <PeopleName>{p.name}</PeopleName>
              {p.meta ? (
                <PeopleMeta $error={!!p.error}>{p.meta}</PeopleMeta>
              ) : null}
            </PeopleText>
          </PeopleItem>
        ))}
      </PeopleList>
    )}
  </PeopleShell>
);

const PosterGroup = ({
  label,
  posters,
  emptyMessage,
  error,
}: {
  label: string;
  posters: Poster[];
  emptyMessage: string;
  error?: boolean;
}) => {
  const valid = posters.filter((p) => !!p.url);
  return (
    <PosterGroupShell>
      <PosterGroupLabel $error={!!error}>{label}</PosterGroupLabel>
      {valid.length === 0 ? (
        <EmptyText $error={!!error}>{emptyMessage}</EmptyText>
      ) : (
        <PosterScroller>
          {valid.map((p, i) => (
            <PosterThumb
              key={`${p.url}-${i}`}
              src={p.url ?? ""}
              onClick={() => p.url && window.open(p.url)}
            />
          ))}
        </PosterScroller>
      )}
    </PosterGroupShell>
  );
};

const Wrapper = styled.div`
  width: 100%;
  flex-direction: column;
  gap: 1.6rem;
`;

const TwoColRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.6rem;
  width: 100%;
  align-items: stretch;

  @media (max-width: 64em) {
    grid-template-columns: 1fr;
  }
`;

const Hero = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.8rem;
  width: 100%;
  background: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 1.2rem;
  box-shadow: ${card.shadow};
  padding: 1.8rem;
  overflow: hidden;

  @media (max-width: 48em) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeroPoster = styled.img`
  flex: 0 0 auto;
  width: 12rem;
  height: 16rem;
  border-radius: 0.8rem;
  object-fit: cover;
  border: 1px solid ${card.border};
  background: ${card.innerBg};
`;

const HeroPosterPlaceholder = styled.div`
  flex: 0 0 auto;
  width: 12rem;
  height: 16rem;
  border-radius: 0.8rem;
  border: 1px dashed ${card.border};
  background: ${card.innerBg};
  color: ${colors.text.hex};
  opacity: 0.45;
  font-size: 1.15rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const HeroBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.6rem;
`;

const HeroEyebrow = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.primary.hex};
  opacity: 0.85;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: 2.6rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${colors.text.hex};
  line-height: 1.1;
`;

const HeroTagline = styled.div`
  font-size: 1.4rem;
  font-style: italic;
  color: ${colors.text.hex};
  opacity: 0.75;
  line-height: 1.4;
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.4rem;
`;

const HeroMetaItem = styled.div`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.6;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Courier New",
    monospace;
`;

const HeroChip = styled.span`
  display: inline-flex;
  padding: 0.15rem 0.7rem;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${colors.primary.hex};
  background: ${card.innerBg};
  border: 1px solid ${card.border};
  border-radius: 999px;
`;

const EditButton = styled.button<{ $error?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 3.2rem;
  padding: 0 1.2rem;
  background: ${({ $error }) =>
    $error ? "rgba(239, 35, 60, 0.10)" : card.innerBg};
  border: 1px solid
    ${({ $error }) => ($error ? colors.error.hex : card.border)};
  border-radius: 0.6rem;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.primary.hex)};
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  & svg {
    font-size: 1.4rem;
  }

  &:hover {
    background: ${({ $error }) =>
      $error ? "rgba(239, 35, 60, 0.18)" : card.accentSoft};
    border-color: ${({ $error }) =>
      $error ? colors.error.hex : colors.primary.hex};
  }
`;

const KvGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.2rem 2rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 32rem) {
    grid-template-columns: 1fr;
  }
`;

const KvShell = styled.div<{ $fullWidth?: boolean }>`
  ${({ $fullWidth }) => ($fullWidth ? "grid-column: 1 / -1;" : "")}
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
`;

const KvLabel = styled.div<{ $error?: boolean }>`
  font-size: 1.15rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.text.hex)};
  opacity: ${({ $error }) => ($error ? 0.95 : 0.55)};
`;

const RequiredMark = styled.span`
  color: ${colors.error.hex};
  margin-left: 0.2rem;
`;

const KvValue = styled.div<{ $missing?: boolean; $multiline?: boolean }>`
  font-size: 1.35rem;
  font-weight: 500;
  color: ${({ $missing }) => ($missing ? colors.text.hex : colors.text.hex)};
  opacity: ${({ $missing }) => ($missing ? 0.45 : 0.95)};
  line-height: 1.45;
  white-space: ${({ $multiline }) => ($multiline ? "pre-wrap" : "normal")};
  word-break: break-word;
`;

const RatingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem 1.2rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 44rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container (max-width: 24rem) {
    grid-template-columns: 1fr;
  }
`;

const RatingShell = styled.div<{ $missing?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 1rem 1.2rem;
  background: ${card.innerBg};
  border: 1px solid
    ${({ $missing }) => ($missing ? colors.error.hex : card.border)};
  border-radius: 0.7rem;
`;

const RatingLabel = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.text.hex};
  opacity: 0.55;
`;

const RatingValue = styled.div<{ $missing?: boolean }>`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${({ $missing }) =>
    $missing ? colors.error.hex : colors.primary.hex};
  opacity: ${({ $missing }) => ($missing ? 0.85 : 1)};
`;

const CrewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.2rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 38rem) {
    grid-template-columns: 1fr;
  }
`;

const PeopleShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1rem 1.2rem;
  background: ${card.innerBg};
  border: 1px solid ${card.border};
  border-radius: 0.8rem;
  min-width: 0;
`;

const PeopleHead = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.text.hex)};
  opacity: ${({ $error }) => ($error ? 0.95 : 0.7)};
`;

const PeopleCount = styled.span`
  font-size: 1.1rem;
  background: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 999px;
  padding: 0.1rem 0.6rem;
  color: ${colors.text.hex};
  opacity: 0.65;
`;

const PeopleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const PeopleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.4rem 0;
`;

const PeopleAvatar = styled.img`
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  border: 1px solid ${card.border};
  object-fit: cover;
  background: ${card.bg};
`;

const PeopleText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const PeopleName = styled.span`
  font-size: 1.3rem;
  font-weight: 500;
  color: ${colors.text.hex};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PeopleMeta = styled.span<{ $error?: boolean }>`
  font-size: 1.15rem;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.text.hex)};
  opacity: ${({ $error }) => ($error ? 0.9 : 0.55)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PeopleEmpty = styled.div<{ $error?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.text.hex)};
  opacity: ${({ $error }) => ($error ? 0.95 : 0.5)};
`;

const AwardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const AwardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 1.2rem;
  background: ${card.innerBg};
  border: 1px solid ${card.border};
  border-radius: 0.8rem;
`;

const AwardYear = styled.div`
  flex: 0 0 auto;
  min-width: 5.4rem;
  text-align: center;
  font-size: 1.6rem;
  font-weight: 700;
  color: ${colors.primary.hex};
  font-variant-numeric: tabular-nums;
  background: ${card.bg};
  border: 1px solid ${card.border};
  padding: 0.5rem 0.8rem;
  border-radius: 0.6rem;
`;

const AwardBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const AwardName = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${colors.text.hex};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AwardMeta = styled.div`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.6;
`;

const AwardWinner = styled.div`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.85;
`;

const StatusPill = styled.span`
  flex: 0 0 auto;
  padding: 0.2rem 0.8rem;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${colors.primary.hex};
  background: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 999px;
`;

const PosterGroupShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  width: 100%;
  min-width: 0;

  & + & {
    margin-top: 1rem;
  }
`;

const PosterGroupLabel = styled.div<{ $error?: boolean }>`
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.text.hex)};
  opacity: ${({ $error }) => ($error ? 0.95 : 0.6)};
`;

const PosterScroller = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const PosterThumb = styled.img`
  height: 10rem;
  width: auto;
  max-width: 100%;
  border-radius: 0.6rem;
  border: 1px solid ${card.border};
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.15s, border-color 0.15s;
  background: ${card.innerBg};

  &:hover {
    transform: translateY(-1px);
    border-color: ${colors.primary.hex};
  }
`;

const EmptyText = styled.span<{ $error?: boolean }>`
  font-size: 1.2rem;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.text.hex)};
  opacity: ${({ $error }) => ($error ? 0.95 : 0.55)};
`;
